import { defineStore } from 'pinia'
import { nextTick, ref, watch } from 'vue'
import { useFetch } from '@/composables/useFetch'
import type { ResultData, Street } from '@/types'

const REGIONS_ENDPOINT = '/wp-json/sppd-gmr/v1/regions'
const DISTRICTS_ENDPOINT = '/wp-json/sppd-gmr/v1/districts'
const TOWNS_ENDPOINT = '/wp-json/sppd-gmr/v1/towns'
const STREETS_ENDPOINT = '/wp-json/sppd-gmr/v1/streets'
const READINGS_DATES_ENDPOINT = '/wp-json/sppd-gmr/v1/readings-dates-data'

export const useLocationStore = defineStore('locationStore', () => {
  // State ref/ reactive
  const regions = ref<string[]>([])
  const selectedRegion = ref<string>('')
  const regionsIsLoading = ref(false)

  const districts = ref<string[]>([])
  const selectedDistrict = ref<string>('')
  const districtsIsLoading = ref(false)

  const towns = ref<string[]>([])
  const selectedTown = ref<string>('')
  const townsIsLoading = ref(false)

  const streets = ref<Street[]>([])
  const selectedStreet = ref<Street | null>(null)
  const streetsIsLoading = ref(false)

  const resultData = ref<ResultData | null>(null)
  const resultDataIsLoading = ref(false)
  const isHydratingSelectors = ref(false)

  // Getters - computed

  // Actions - functions
  async function fetchRegions() {
    console.info('Fetching regions...')
    regionsIsLoading.value = true
    try {
      regions.value = [] // Clear previous data
      const response = await useFetch(REGIONS_ENDPOINT)
      regions.value = response.items
    } catch (err) {
      console.error('Error fetching regions:', err)
      throw err
    } finally {
      regionsIsLoading.value = false
    }
  }

  async function fetchDistricts() {
    console.info(`Fetching districts for region: "${selectedRegion.value}"`)
    districtsIsLoading.value = true

    try {
      if (!selectedRegion.value) {
        districts.value = [] // Clear districts if no region is selected
        return
      }

      districts.value = [] // Clear previous data
      const response = await useFetch(
        `${DISTRICTS_ENDPOINT}?region=${encodeURIComponent(selectedRegion.value)}`,
      )
      districts.value = response.items
    } catch (err) {
      console.error('Error fetching districts:', err)
      throw err
    } finally {
      districtsIsLoading.value = false
    }
  }

  async function fetchTowns() {
    console.info(`Fetching towns for district: "${selectedDistrict.value}"`)
    townsIsLoading.value = true

    try {
      if (!selectedDistrict.value) {
        towns.value = [] // Clear towns if no district is selected
        return
      }

      towns.value = [] // Clear previous data
      const response = await useFetch(
        `${TOWNS_ENDPOINT}?district=${encodeURIComponent(selectedDistrict.value)}`,
      )
      towns.value = response.items
    } catch (err) {
      console.error('Error fetching towns:', err)
      throw err
    } finally {
      townsIsLoading.value = false
    }
  }

  async function fetchStreets(query: string) {
    console.log(`Fetching streets for town: "${selectedTown.value}" with query: "${query}"`)
    streetsIsLoading.value = true

    try {
      if (!selectedTown.value) {
        streets.value = [] // Clear streets if no town is selected
        return
      }
      streets.value = [] // Clear previous data
      const response = await useFetch(
        `${STREETS_ENDPOINT}?town=${encodeURIComponent(selectedTown.value)}&search=${encodeURIComponent(query)}`,
      )

      streets.value = response.items
    } catch (err) {
      console.error('Error fetching streets:', err)
      throw err
    } finally {
      streetsIsLoading.value = false
    }
  }

  async function fetchReadingsDatesData(streetNumericCode: string, fillSelectors = false) {
    resultDataIsLoading.value = true

    try {
      const response = await useFetch<ResultData>(
        `${READINGS_DATES_ENDPOINT}/${encodeURIComponent(streetNumericCode)}`,
      )
      resultData.value = response
      if (fillSelectors) {
        // Fill selectors based on resultData.record
        selectedRegion.value = response.record.region
        selectedDistrict.value = response.record.district
        selectedTown.value = response.record.town
        selectedStreet.value = {
          street: response.record.street,
          street_numeric_code: response.record.street_numeric_code,
        }
        // Keep watchers muted until queued reactions are flushed.
        await nextTick()
      }
    } catch (err) {
      console.error('Error fetching result data:', err)
      throw err
    } finally {
      resultDataIsLoading.value = false
    }
  }

  function resetSelections(model: 'region' | 'district' | 'town') {
    console.info(`Resetting state for model: "${model}" and its dependents`)
    switch (model) {
      case 'region':
        selectedDistrict.value = ''
        selectedTown.value = ''
        selectedStreet.value = null
        districts.value = []
        towns.value = []
        streets.value = []
        resultData.value = null
        break
      case 'district':
        selectedTown.value = ''
        towns.value = []
        selectedStreet.value = null
        streets.value = []
        resultData.value = null
        break
      case 'town':
        selectedStreet.value = null
        streets.value = []
        resultData.value = null
        break
    }
    clearUrlParams()
  }

  function loadByUrlParams() {
    const searchParams = new URLSearchParams(window.location.search)
    const streetNumericCode = searchParams.get('street-numeric-code')
    if (streetNumericCode) {
      console.info(`URL parameter "street-numeric-code" found: "${streetNumericCode}", loading data...`)
      isHydratingSelectors.value = true
        fetchReadingsDatesData(streetNumericCode, true).then(() => {
          fetchRegions()
          fetchDistricts()
          fetchTowns()
          fetchStreets('')
        }).catch((err) => {
          console.error('Error fetching result data from URL parameter on initial load:', err)
          isHydratingSelectors.value = false
        }).finally(() => {          
          isHydratingSelectors.value = false
        }
        )
    } else {
      // Load default data if no URL parameter is present
      fetchRegions()
    }
  }

  function setUrlParams(streetNumericCode: string | null) {
    const searchURL = new URL(window.location.toString())
    if (!streetNumericCode) {
      clearUrlParams()
      return
    }

    searchURL.searchParams.set('street-numeric-code', streetNumericCode)
    window.history.pushState({}, '', searchURL)
  }

  function clearUrlParams() {
    console.info('Clearing URL parameters related to street selection')
    const searchURL = new URL(window.location.toString())
    searchURL.searchParams.delete('street-numeric-code')
    window.history.pushState({}, '', searchURL)
  }

  // Watcheer - react to changes in selectedRegion
  watch(selectedRegion, async (newRegion, oldRegion) => {
    if (isHydratingSelectors.value) {
      return
    }

    if (newRegion === oldRegion) {
      return
    }

    console.info(`Selected region changed from "${oldRegion}" to "${newRegion}"`)
    // Reset dependent state
    resetSelections('region')

    if (!newRegion) {
      return
    }

    try {
      console.info('Region changed, fetching districts...') // Debug log
      await fetchDistricts()
    } catch (err) {
      console.error('Error fetching districts after region change:', err)
    }
  })

  // Watcheer - react to changes in selectedDistrict
  watch(selectedDistrict, async (newDistrict, oldDistrict) => {
    if (isHydratingSelectors.value) {
      return
    }

    if (newDistrict === oldDistrict) {
      return
    }

    console.info(`Selected district changed from "${oldDistrict}" to "${newDistrict}"`)
    // Reset dependent state
    resetSelections('district')

    if (!newDistrict) {
      return
    }

    try {
      console.info('District changed, fetching towns...') // Debug log
      await fetchTowns()
    } catch (err) {
      console.error('Error fetching towns after district change:', err)
    }
  })

  // Watcheer - react to changes in selectedTown
  watch(selectedTown, async (newTown, oldTown) => {
    if (isHydratingSelectors.value) {
      return
    }

    if (newTown === oldTown) {
      return
    }

    console.info(`Selected town changed from "${oldTown}" to "${newTown}"`)

    // Reset dependent state
    resetSelections('town')

    if (!newTown) {
      return
    }

    try {
      console.info('Town changed, fetching streets...') // Debug log
      await fetchStreets('')
    } catch (err) {
      console.error('Error fetching streets after town change:', err)
    }
  })

  // Watcheer - react to changes in selectedStreet
  watch(selectedStreet, async (newStreet, oldStreet) => {
    if (isHydratingSelectors.value) {
      return
    }

    if (newStreet === oldStreet) {
      return
    }

    console.info(
      `Selected street changed from "${oldStreet?.street_numeric_code}" to "${newStreet?.street_numeric_code}"`,
    )
    setUrlParams(newStreet ? newStreet.street_numeric_code : null)

    if (!newStreet) {
      return
    }

    try {
      console.info('Street changed, fetching readings dates data...') // Debug log
      await fetchReadingsDatesData(newStreet.street_numeric_code)
    } catch (err) {
      console.error('Error fetching readings dates data after street change:', err)
    }
  })

  return {
    // State
    regions,
    regionsIsLoading,
    selectedRegion,
    districts,
    selectedDistrict,
    districtsIsLoading,
    towns,
    selectedTown,
    townsIsLoading,
    streets,
    selectedStreet,
    streetsIsLoading,
    resultData,
    resultDataIsLoading,
    // Getters
    // Actions
    loadByUrlParams,
    fetchRegions,
    fetchDistricts,
    fetchTowns,
    fetchStreets,
  }
})
